import {
  Box,
  Button,
  Checkbox,
  FormHelperText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { convertJsonSchemaToZod } from '@nohara-dxdev/mt-json-schema';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ItemTemplateEntityWithSchema } from '@/types/api';

interface AttributeField {
  key: string;
  name: string;
  type: string;
  enum?: string[];
  order: number;
}

export interface AttributeBulkEditFormProps {
  template: Pick<ItemTemplateEntityWithSchema, 'jsonSchema'>;
  mainInstanceAttributes: Record<string, unknown>;
  onExecute: (selectedAttributes: Record<string, unknown>) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const clearKey = (obj: Record<string, string>, key: string): Record<string, string> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [key]: _, ...rest } = obj;
  return rest;
};

export const AttributeBulkEditForm = ({
  template,
  mainInstanceAttributes,
  onExecute,
  onCancel,
  isSubmitting = false,
}: AttributeBulkEditFormProps) => {
  const { t } = useTranslation();

  // Derive attribute fields from JSON schema sorted by x-order
  const attributeFields = useMemo((): AttributeField[] => {
    const schema = template.jsonSchema;
    if (typeof schema.properties !== 'object' || schema.type !== 'object') {
      return [];
    }

    const fields = Object.entries(schema.properties).map(([key, property]) => {
      const prop = property as Record<string, unknown>;
      return {
        key,
        name: (prop.title as string | undefined) ?? key,
        type: (prop.type as string | undefined) ?? 'string',
        enum: prop.enum as string[] | undefined,
        order: (prop['x-order'] as number | undefined) ?? Number.MAX_SAFE_INTEGER,
      };
    });

    return fields.sort((a, b) => a.order - b.order);
  }, [template.jsonSchema]);

  // Local state: selected keys
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // Local state: edited values (per key)
  const [editedValues, setEditedValues] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = {};
    for (const field of attributeFields) {
      initial[field.key] = mainInstanceAttributes[field.key] ?? '';
    }
    return initial;
  });

  // Local state: per-field validation errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const allSelected = attributeFields.length > 0 && selectedKeys.size === attributeFields.length;
  const someSelected = selectedKeys.size > 0 && selectedKeys.size < attributeFields.length;

  const handleToggleRow = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    setFieldErrors((prev) => clearKey(prev, key));
  };

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(attributeFields.map((f) => f.key)));
    }
  };

  const handleValueChange = (key: string, value: unknown) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => clearKey(prev, key));
  };

  const handleExecute = () => {
    if (selectedKeys.size === 0) return;

    // Build the attributes payload of only selected keys
    const selectedAttributes: Record<string, unknown> = {};
    for (const key of selectedKeys) {
      selectedAttributes[key] = editedValues[key];
    }

    // Validate against JSON schema
    const zodSchema = convertJsonSchemaToZod(template.jsonSchema as object);
    const merged = { ...mainInstanceAttributes, ...selectedAttributes };
    const result = zodSchema.safeParse(merged);

    if (!result.success) {
      const errors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const fieldKey = issue.path[0] as string | undefined;
        if (fieldKey && selectedKeys.has(fieldKey)) {
          errors[fieldKey] = issue.message;
        }
      }
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
    }

    onExecute(selectedAttributes);
  };

  if (attributeFields.length === 0) {
    return (
      <Box>
        <Typography
          variant="body2"
          color="textSecondary"
          data-testid="no-attributes-message"
          sx={{ p: 2 }}
        >
          {t('items.bulkEdit.noAttributes')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2 }}>
          <Button data-testid="cancel-button" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        </Box>
      </Box>
    );
  }

  const executeDisabled = isSubmitting || selectedKeys.size === 0;

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {t('items.bulkEdit.attributesTitle')}
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  slotProps={{ input: { 'data-testid': 'header-checkbox' } as object }}
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleToggleAll}
                  disabled={isSubmitting}
                />
              </TableCell>
              <TableCell>{t('items.bulkEdit.column.attribute')}</TableCell>
              <TableCell>{t('items.bulkEdit.column.value')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attributeFields.map((field) => {
              const isSelected = selectedKeys.has(field.key);
              const fieldError = fieldErrors[field.key];
              const value = editedValues[field.key] ?? '';

              return (
                <TableRow key={field.key}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      slotProps={{
                        input: { 'data-testid': `row-checkbox-${field.key}` } as object,
                      }}
                      checked={isSelected}
                      onChange={() => {
                        handleToggleRow(field.key);
                      }}
                      disabled={isSubmitting}
                    />
                  </TableCell>
                  <TableCell>{field.name}</TableCell>
                  <TableCell>
                    {field.enum ? (
                      <>
                        <Select
                          value={value === '' ? '__blank__' : (value as string)}
                          onChange={(e) => {
                            const next =
                              e.target.value === '__blank__' ? undefined : e.target.value;
                            handleValueChange(field.key, next);
                          }}
                          disabled={isSubmitting}
                          size="small"
                          fullWidth
                          error={!!fieldError}
                        >
                          <MenuItem value="__blank__">&nbsp;</MenuItem>
                          {field.enum.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {fieldError && <FormHelperText error>{fieldError}</FormHelperText>}
                      </>
                    ) : field.type === 'number' ? (
                      <TextField
                        type="number"
                        value={value as string}
                        onChange={(e) => {
                          handleValueChange(
                            field.key,
                            e.target.value === '' ? '' : Number(e.target.value),
                          );
                        }}
                        disabled={isSubmitting}
                        size="small"
                        fullWidth
                        error={!!fieldError}
                        helperText={fieldError}
                      />
                    ) : (
                      <TextField
                        value={value as string}
                        onChange={(e) => {
                          handleValueChange(field.key, e.target.value);
                        }}
                        disabled={isSubmitting}
                        size="small"
                        fullWidth
                        error={!!fieldError}
                        helperText={fieldError}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          pt: 2,
        }}
      >
        <Button data-testid="cancel-button" onClick={onCancel} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          data-testid="execute-button"
          variant="contained"
          color="primary"
          disabled={executeDisabled}
          onClick={handleExecute}
        >
          {t('items.bulkEdit.execute')}
        </Button>
      </Box>
    </Box>
  );
};
